/*
  Pix on Puzzles - numbered back add-on in the cart (assets/pix-cart-addons.js, loaded by snippets/cart-products.liquid, which draws the cart drawer and the cart page)

  The numbered back is its own product ("numbered-puzzle-back"). Each of its cart lines carries "_Pix For", the
  "_Pix Edit" id of the puzzle it belongs to (snippets/cart-products.liquid shows it inside that puzzle's row).
  This script:
    - adds it from the cart ("Add a numbered back" under a puzzle), and
    - keeps it in step with its puzzle: the cart marks a numbered back line that needs fixing with
      data-pix-nb-fix (the quantity it should have: its puzzle's quantity, or 0 when its puzzle is gone),
      and this changes it through Horizon's cart, so the drawer, the cart page and the cart count all update.
*/
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/* while this runs, a numbered back whose puzzle has gone stays out of sight until it is taken out */
document.documentElement.classList.add('pix-nb-js');

const tried = new Map();      /* fix -> when it was last tried, so a fix that keeps failing can't loop */
let fixing = false;
let timer = 0;

function cartRoot() {
  const r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return typeof r === 'string' && r ? r : '/';
}
function cartComponent() {
  return document.querySelector('cart-items-component');
}
function ga(name, extra) {
  try { if (window.gtag) window.gtag('event', name, Object.assign({ tool: 'pix_cart' }, extra || {})); } catch (e) {}
}

/* ---------- keep each numbered back in step with its puzzle ---------- */
function fixOne() {
  if (fixing) return;
  const comp = cartComponent();
  if (!comp || typeof comp.updateQuantity !== 'function') return;
  const rows = comp.querySelectorAll('[data-pix-nb-fix][data-pix-nb-line]');
  for (const row of rows) {
    const qty = parseInt(row.getAttribute('data-pix-nb-fix'), 10);
    const line = parseInt(row.getAttribute('data-pix-nb-line'), 10);
    const sig = (row.getAttribute('data-key') || line) + ':' + qty;
    if (!(line > 0) || !(qty >= 0) || Date.now() - (tried.get(sig) || 0) < 5000) continue;
    tried.set(sig, Date.now());
    fixing = true;
    comp.updateQuantity({ line, quantity: qty, action: qty > 0 ? 'change' : 'clear' });
    setTimeout(() => { fixing = false; }, 400);
    return;
  }
}
function later(ms) {
  clearTimeout(timer);
  timer = setTimeout(() => { fixing = false; fixOne(); }, ms);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const p = event.promise;
  if (p && p.then) p.then(() => later(250), () => later(250));
  else later(600);
});

/* ---------- "Add a numbered back" in the cart ---------- */
document.addEventListener('click', (e) => {
  const btn = e.target.closest && e.target.closest('[data-pix-nb-add]');
  if (!btn || btn.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  const variant = btn.getAttribute('data-variant');
  const qty = Math.max(1, parseInt(btn.getAttribute('data-qty'), 10) || 1);
  if (!variant) return;
  btn.setAttribute('aria-busy', 'true');
  const cta = btn.querySelector('.pix-nb__cta');
  const ctaText = cta ? cta.textContent : '';
  if (cta) cta.textContent = 'Adding…';

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((c) => { if (c.dataset.sectionId) sectionIds.push(c.dataset.sectionId); });

  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'cart',
    lines: [{ merchandiseId: variant, quantity: qty }],
    promise: deferred.promise,
  }));

  fetch(cartRoot() + 'cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [{ id: Number(variant), quantity: qty, properties: { For: btn.getAttribute('data-label') || 'Puzzle', '_Pix For': btn.getAttribute('data-for') || '' } }],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  })
    .then((r) => r.json().then((j) => { if (!r.ok || j.status) throw new Error(j.description || j.message || 'HTTP ' + r.status); return j; }))
    .then((added) => fetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, sections: added.sections, itemCount: qty, source: 'pix-numbered-back', didError: false },
        });
        ga('numbered_back_toggled', { on: 'yes', where: 'cart', pieces: Number(btn.getAttribute('data-pieces')) || 0 });
      }))
    .catch((err) => {
      console.warn('[Pix cart] numbered back not added:', err && err.message);
      deferred.reject(err);
      btn.removeAttribute('aria-busy');
      if (cta) cta.textContent = ctaText;
      const t = btn.querySelector('small');
      if (t) t.textContent = 'That did not go through. Please try again.';
    });
});

/* ---------- removing it from its chip ---------- */
document.addEventListener('click', (e) => {
  if (e.target.closest && e.target.closest('.pix-nb__remove')) ga('numbered_back_toggled', { on: 'no', where: 'cart' });
}, true);

/* a page that opens with a line out of step (like a puzzle removed on another tab) */
customElements.whenDefined('cart-items-component').then(() => later(300));

/* ---------- the cart picture, bigger: "Together", "Puzzle" and "Tin lid" views, each can be zoomed ----------
   Opens in place of snippets/cart-products.liquid's plain lightbox. Pinch, double-tap, scroll or +/- to zoom;
   drag to look around. The puzzle and lid pictures come from the line's own properties (via /cart.js). */
let zDlg = null, zImg = null, zTabs = null, zState = { s: 1, x: 0, y: 0 }, cartP = null, cartAt = 0;
function zStyle() {
  const st = document.createElement('style');
  st.textContent = '.pixz{width:min(96vw,980px);max-width:none;max-height:none;margin:auto;padding:0;border:0;border-radius:18px;background:#fff;color:#22314A;font-family:Figtree,system-ui,sans-serif;box-shadow:0 24px 70px rgb(16 24 40/.32)}' +
    '.pixz::backdrop{background:rgb(17 29 48/.78)}.pixz-box{display:flex;flex-direction:column;height:min(88dvh,860px)}' +
    '.pixz-top{display:flex;align-items:center;gap:10px;padding:10px 10px 8px 14px}.pixz-tabs{flex:1;display:flex;gap:6px;overflow-x:auto;scrollbar-width:none}' +
    '.pixz-tabs button{flex:none;min-height:40px;padding:0 14px;border:1.5px solid #E4DCCB;border-radius:20px;background:#fff;color:#22314A;font:inherit;font-weight:700;font-size:.92rem;cursor:pointer}' +
    '.pixz-tabs button[aria-selected="true"]{background:#2D5DA8;border-color:#2D5DA8;color:#fff}' +
    '.pixz-x{flex:none;width:44px;height:44px;border:0;border-radius:50%;background:#F3EDE0;color:#22314A;font-size:1.6rem;line-height:1;cursor:pointer}' +
    '.pixz-stage{position:relative;flex:1;min-height:0;overflow:hidden;background:#F3EEE6;touch-action:none;cursor:zoom-in}.pixz-stage.is-in{cursor:grab}' +
    '.pixz-img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;transform-origin:50% 50%;user-select:none;-webkit-user-drag:none;max-width:none}' +
    '.pixz-load{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-weight:700;color:#52607A}.pixz-load[hidden]{display:none}' +
    '.pixz-bar{display:flex;align-items:center;justify-content:center;gap:14px;padding:8px 12px calc(10px + env(safe-area-inset-bottom));font-size:.86rem;font-weight:600;color:#52607A}' +
    '.pixz-bar button{width:44px;height:44px;border:1.5px solid #C9D6EA;border-radius:50%;background:#fff;color:#22314A;font:inherit;font-size:1.4rem;font-weight:800;cursor:pointer}' +
    '@media (max-width:749px){.pixz{width:100vw;height:100dvh;max-height:100dvh;border-radius:0}.pixz-box{height:100dvh}.pixz-top{padding-top:calc(10px + env(safe-area-inset-top))}}';
  document.head.appendChild(st);
}
function zFit() {      /* the picture's size on screen at zoom 1 */
  const r = zImg.parentNode.getBoundingClientRect(), nw = zImg.naturalWidth || 1, nh = zImg.naturalHeight || 1, k = Math.min(r.width / nw, r.height / nh);
  return { w: nw * k, h: nh * k, W: r.width, H: r.height };
}
function zApply() {
  const f = zFit(), S = zState;
  S.s = Math.min(6, Math.max(1, S.s));
  const mx = Math.max(0, (f.w * S.s - f.W) / 2), my = Math.max(0, (f.h * S.s - f.H) / 2);
  S.x = Math.min(mx, Math.max(-mx, S.x)); S.y = Math.min(my, Math.max(-my, S.y));
  zImg.style.transform = 'translate(' + S.x + 'px,' + S.y + 'px) scale(' + S.s + ')';
  zImg.parentNode.classList.toggle('is-in', S.s > 1.01);
}
function zZoomAt(ns, px, py) {      /* zoom to ns, keeping the point (px, py) from the stage's centre where it is */
  const S = zState, k = Math.min(6, Math.max(1, ns)) / S.s;
  S.x = px - (px - S.x) * k; S.y = py - (py - S.y) * k; S.s *= k; zApply();
}
function zShow(src) {
  zState = { s: 1, x: 0, y: 0 };
  const load = zDlg.querySelector('.pixz-load');
  load.textContent = 'Loading…'; load.hidden = false; zImg.style.visibility = 'hidden';
  zImg.onload = () => { load.hidden = true; zImg.style.visibility = ''; zApply(); };
  zImg.onerror = () => { load.textContent = 'This picture could not be loaded.'; };
  zImg.src = src; zImg.style.transform = '';
}
function zBuild() {
  zStyle();
  zDlg = document.createElement('dialog'); zDlg.className = 'pixz'; zDlg.setAttribute('aria-label', 'Your puzzle, larger');
  zDlg.innerHTML = '<div class="pixz-box"><div class="pixz-top"><div class="pixz-tabs" role="tablist"></div><button type="button" class="pixz-x" aria-label="Close">&times;</button></div>' +
    '<div class="pixz-stage"><img class="pixz-img" alt="" draggable="false"><div class="pixz-load">Loading…</div></div>' +
    '<div class="pixz-bar"><button type="button" data-z="out" aria-label="Zoom out">&minus;</button><span>Pinch or double-tap to zoom</span><button type="button" data-z="in" aria-label="Zoom in">+</button></div></div>';
  document.body.appendChild(zDlg);
  zImg = zDlg.querySelector('.pixz-img'); zTabs = zDlg.querySelector('.pixz-tabs');
  const stage = zDlg.querySelector('.pixz-stage'), ptrs = new Map();
  let pinch = null, drag = null, lastTap = 0, lastXY = null;
  const local = (e) => { const r = stage.getBoundingClientRect(); return { x: e.clientX - r.left - r.width / 2, y: e.clientY - r.top - r.height / 2 }; };
  zDlg.addEventListener('click', (e) => {
    if (e.target === zDlg || e.target.closest('.pixz-x')) { zDlg.close(); return; }
    const z = e.target.closest('[data-z]'); if (z) zZoomAt(zState.s * (z.dataset.z === 'in' ? 1.6 : 1 / 1.6), 0, 0);
    const t = e.target.closest('[data-src]');
    if (t) { zTabs.querySelectorAll('button').forEach((b) => b.setAttribute('aria-selected', String(b === t))); zShow(t.dataset.src); ga('cart_preview_view', { view: t.dataset.view }); }
  });
  stage.addEventListener('pointerdown', (e) => {
    try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    ptrs.set(e.pointerId, local(e));
    if (ptrs.size === 2) { const [a, b] = [...ptrs.values()]; pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: zState.s, m: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, x: zState.x, y: zState.y }; drag = null; }
    else drag = { p: local(e), x: zState.x, y: zState.y, moved: false };
  });
  stage.addEventListener('pointermove', (e) => {
    if (!ptrs.has(e.pointerId)) return;
    ptrs.set(e.pointerId, local(e));
    if (pinch && ptrs.size >= 2) {
      const [a, b] = [...ptrs.values()], k = (Math.hypot(a.x - b.x, a.y - b.y) || 1) / pinch.d, ns = Math.min(6, Math.max(1, pinch.s * k)) / pinch.s;
      zState.s = pinch.s * ns; zState.x = pinch.m.x - (pinch.m.x - pinch.x) * ns; zState.y = pinch.m.y - (pinch.m.y - pinch.y) * ns; zApply(); return;
    }
    if (!drag) return;
    const p = local(e), dx = p.x - drag.p.x, dy = p.y - drag.p.y;
    if (Math.abs(dx) + Math.abs(dy) > 6) drag.moved = true;
    if (zState.s > 1.01) { zState.x = drag.x + dx; zState.y = drag.y + dy; zApply(); }
  });
  const up = (e) => {
    const p = ptrs.get(e.pointerId); ptrs.delete(e.pointerId);
    if (ptrs.size < 2) pinch = null;
    if (drag && !drag.moved && p && e.type === 'pointerup') {      /* a double tap zooms in on that spot, or back out */
      const now = Date.now();
      if (now - lastTap < 320 && lastXY && Math.hypot(p.x - lastXY.x, p.y - lastXY.y) < 30) { zZoomAt(zState.s > 1.01 ? 1 : 2.5, p.x, p.y); lastTap = 0; ga('cart_preview_zoom', {}); }
      else { lastTap = now; lastXY = p; }
    }
    if (!ptrs.size) drag = null;
  };
  stage.addEventListener('pointerup', up); stage.addEventListener('pointercancel', up);
  stage.addEventListener('wheel', (e) => { e.preventDefault(); const p = local(e); zZoomAt(zState.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15), p.x, p.y); }, { passive: false });
  window.addEventListener('resize', () => { if (zDlg.open) zApply(); });
}
function lineProps(key) {      /* the cart line's properties, from /cart.js (kept for a few seconds) */
  if (!cartP || Date.now() - cartAt > 8000) {
    cartAt = Date.now();
    cartP = fetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
  }
  return cartP.then((c) => { const it = c && (c.items || []).find((i) => i.key === key); return (it && it.properties) || {}; });
}
function puzzleSrc(p) {      /* the puzzle picture with its lines: in the order's download link, or the print file */
  if (p['_Pix Puzzle']) return p['_Pix Puzzle'];
  try {
    const u = new URL(p['_Download files']), m = u.pathname.match(/\/order\/(.+)$/), v = u.searchParams.get('v');
    if (m && v) return u.origin + '/file/' + m[1] + '/' + v;
  } catch (e) {}
  return p['_Print file'] || '';
}
window.addEventListener('click', (e) => {
  const trig = e.target.closest && e.target.closest('.pix-cart-preview-trigger');
  if (!trig) return;
  e.preventDefault(); e.stopPropagation();      /* instead of the plain lightbox */
  const main = trig.getAttribute('data-pix-preview-src'); if (!main) return;
  if (!zDlg) zBuild();
  zTabs.innerHTML = ''; zShow(main);
  if (typeof zDlg.showModal === 'function') zDlg.showModal(); else zDlg.setAttribute('open', '');
  ga('cart_preview_opened', {});
  const row = trig.closest('[data-key]');
  if (!row) return;
  lineProps(row.getAttribute('data-key')).then((p) => {
    const views = [['together', 'Together', main], ['puzzle', 'Puzzle', puzzleSrc(p)], ['lid', 'Tin lid', p['_Tin lid file'] || '']].filter((v) => v[2]);
    if (views.length < 2 || !zDlg.open) return;
    zTabs.innerHTML = views.map((v, i) => '<button type="button" role="tab" aria-selected="' + (i === 0) + '" data-view="' + v[0] + '" data-src="' + v[2].replace(/"/g, '&quot;') + '">' + v[1] + '</button>').join('');
  });
}, true);